import { useEffect, useRef, useState } from "react";
import { ExternalLink, Loader2, AlertTriangle, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

/**
 * Parts Catalogue (Phase 1) — embeds partsinfo.in inside the Fender shell
 * via a plain iframe. No SSO, no data sync, no postMessage. This is
 * deliberately the minimum-effort integration.
 *
 * Load detection: browsers do NOT fire an `error` event when a frame is
 * blocked by X-Frame-Options or CSP frame-ancestors — the `load` event
 * still fires with a blank frame in some cases, and never in others.
 * We use a 12-second watchdog: if the iframe hasn't reported `load` by
 * then, we show the fallback state.
 */

const PARTSINFO_URL = "https://partsinfo.in";
const LOAD_TIMEOUT_MS = 12000;

export default function PartsCatalogue() {
  const { user, garage } = useAuth();
  const [state, setState] = useState("loading"); // "loading" | "loaded" | "blocked"
  const iframeRef = useRef(null);
  const timerRef = useRef(null);

  // Fire analytics event once on mount.
  useEffect(() => {
    if (!user?.id) return;
    api.post("/analytics/track", {
      event: "parts_catalogue_opened",
      user_id: user.id,
      garage_id: garage?.id || user.garage_id || null,
      timestamp: new Date().toISOString(),
    }).catch(() => { /* fire-and-forget */ });
     
  }, [user?.id, garage?.id]);

  // Load watchdog.
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setState((s) => (s === "loading" ? "blocked" : s));
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(timerRef.current);
  }, []);

  const onIframeLoad = () => {
    clearTimeout(timerRef.current);
    setState("loaded");
  };

  const openInNewTab = () => {
    window.open(PARTSINFO_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-full" data-testid="parts-catalogue-page">
      {/* ---------- Page header ---------- */}
      <header className="px-4 md:px-6 py-4 border-b border-[#E4E7EC] bg-white">
        <div className="flex items-start md:items-center gap-3 flex-col md:flex-row md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[#a8dadc]/40 grid place-items-center text-[#1d3557]">
                <BookOpen size={16} strokeWidth={2} />
              </div>
              <h1 className="text-[18px] font-bold text-[#1d3557]" data-testid="parts-catalogue-title">
                Parts Catalogue
              </h1>
            </div>
            <p className="text-[12.5px] text-[#667085] mt-1">
              Browse automobile spare-parts catalogues with PartsInfo.
            </p>
            <p className="text-[10.5px] text-[#98A2B3] uppercase tracking-widest mt-1">
              Powered by PartsInfo
            </p>
          </div>
          <button
            type="button"
            onClick={openInNewTab}
            data-testid="parts-catalogue-open-new-tab"
            className="h-9 px-3 rounded-md border border-[#E4E7EC] bg-white text-[12.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] hover:border-[#457b9d] inline-flex items-center gap-1.5 whitespace-nowrap"
          >
            <ExternalLink size={13} /> Open in New Tab
          </button>
        </div>
      </header>

      {/* ---------- Content area ---------- */}
      <div
        className="relative bg-[#F7F9FC]"
        style={{ height: "calc(100vh - 56px - 96px)" }}
        data-testid="parts-catalogue-frame-wrap"
      >
        {state === "blocked" ? (
          <BlockedFallback onOpenNewTab={openInNewTab} />
        ) : (
          <>
            <iframe
              ref={iframeRef}
              src={PARTSINFO_URL}
              title="PartsInfo Spare Parts Catalogue"
              onLoad={onIframeLoad}
              allow="clipboard-read; clipboard-write"
              referrerPolicy="no-referrer"
              className="w-full h-full block bg-white"
              style={{ border: 0 }}
              data-testid="parts-catalogue-iframe"
            />
            {state === "loading" && <LoadingOverlay />}
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Sub-components ----------

const LoadingOverlay = () => (
  <div
    className="absolute inset-0 grid place-items-center bg-white/90 backdrop-blur-sm"
    data-testid="parts-catalogue-loading"
  >
    <div className="flex flex-col items-center gap-2 text-[#457b9d]">
      <Loader2 size={22} className="animate-spin" strokeWidth={2} />
      <div className="text-[13px] font-medium">Loading Parts Catalogue...</div>
    </div>
  </div>
);

const BlockedFallback = ({ onOpenNewTab }) => (
  <div
    className="h-full grid place-items-center px-6"
    data-testid="parts-catalogue-blocked"
  >
    <div className="max-w-md text-center">
      <div className="w-12 h-12 rounded-full bg-[#F79009]/15 text-[#F79009] grid place-items-center mx-auto mb-4">
        <AlertTriangle size={22} strokeWidth={2} />
      </div>
      <h2 className="text-[16px] font-semibold text-[#1d3557]">
        Parts Catalogue could not be loaded inside Fender.
      </h2>
      <p className="text-[12.5px] text-[#667085] mt-2 leading-relaxed">
        The catalogue site may be blocking embedded frames. You can open it
        in a new browser tab instead — your Fender session will remain
        untouched.
      </p>
      <button
        type="button"
        onClick={onOpenNewTab}
        data-testid="parts-catalogue-blocked-open"
        className="mt-5 h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5"
      >
        <ExternalLink size={13} /> Open PartsInfo in New Tab
      </button>
    </div>
  </div>
);
